<template>
  <div ref="timelinesList" class="ot-TimelinesList">
    <OkrTimelinesListItem
      v-for="elementId in modelValue"
      :key="elementId"
      :ref="setReference"
      :active-view="activeView"
      :depth="depth"
      :expanded="listState.filtersValues[FILTERS_KEYS.EXPANDED_ITEMS][`${elementId}-${depth}`]"
      :min-max-date="minMaxDate"
      :objective="listState.okrElements[elementId]"
      :one-day-width="oneDayWidth"
      @update-elements="updateElements"
      @on-okr-element-updated="onOkrElementUpdated($event)"
      @edit-okr-element="editElement(listState.okrElements[elementId])"
    />
  </div>

  <ObjectiveViewRowActions
    ref="actions"
    :depth="depth"
    :source="EVENT_SOURCES.ROADMAP"
    @update="updateElements"
  />
</template>

<script>
import { isUndefined } from 'lodash'
import { defineComponent } from 'vue'

import { EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { listStateInjectionKey } from '@/utils/injection-keys'
import { applyUpdatedParametersToElement } from '@/utils/objectives'
import { FILTERS_KEYS } from '@/utils/okr-elements/filters'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import ObjectiveViewRowActions from '@/components/objectives/ObjectiveViewRowActions'
import OkrTimelinesListItem from '@/components/objectives/roadmap/OkrTimelinesListItem'

export default defineComponent({
  name: 'OkrTimelinesList',

  components: { ObjectiveViewRowActions, OkrTimelinesListItem },

  inject: {
    listState: {
      from: listStateInjectionKey
    }
  },

  inheritAttrs: false,

  props: {
    modelValue: {
      type: Array,
      required: true
    },

    depth: {
      type: Number,
      default: 0
    },

    oneDayWidth: {
      type: Number,
      required: true
    },

    minMaxDate: {
      type: Object,
      required: true
    },

    activeView: {
      type: String,
      required: true
    }
  },

  emits: {
    'on-okr-element-updated': null,
    'update-elements': null,
    'update:childListHeight': null
  },

  data() {
    return {
      references: [],
      resizeObserver: null
    }
  },

  computed: {
    EVENT_SOURCES: () => EVENT_SOURCES,

    FILTERS_KEYS: () => FILTERS_KEYS
  },

  mounted() {
    this.initResizeObserver()
  },

  beforeUnmount() {
    this.resizeObserver.disconnect()
    this.resizeObserver = null
    this.$emit('update:childListHeight', 0)
  },

  beforeUpdate() {
    this.references = []
  },

  methods: {
    async initResizeObserver() {
      await this.$nextTick()
      const { timelinesList } = this.$refs
      this.resizeObserver = new ResizeObserver(() => {
        this.$emit('update:childListHeight', timelinesList.offsetHeight)
      })
      this.resizeObserver.observe(timelinesList)
    },

    updateElements(eventData) {
      this.$emit('update-elements', eventData)
    },

    editElement(element) {
      this.$refs.actions.editOkrElement(element)
    },

    /** @public */
    refreshPositions() {
      this.references.forEach(item => {
        item.refreshPositions()
      })
    },

    setReference(reference) {
      if (reference) {
        this.references.push(reference)
      }
    },

    onOkrElementUpdated(updatedOkrElementInfo) {
      const { elementId } = updatedOkrElementInfo

      this.listState.okrElements[elementId] = {
        ...applyUpdatedParametersToElement({
          element: this.listState.okrElements[elementId],
          updatedOkrElementInfo
        })
      }

      const PARAMETERS_TO_UPDATE = [
        OKR_ELEMENT_KEYS.ELEMENT_START_DATE,
        OKR_ELEMENT_KEYS.DUE_DATE,
        OKR_ELEMENT_KEYS.AUTOMATIC_DUE_DATE,
        OKR_ELEMENT_KEYS.AUTOMATIC_ELEMENT_START_DATE
      ]

      PARAMETERS_TO_UPDATE.forEach(param => {
        this.listState.okrElements[elementId][param] = updatedOkrElementInfo[param]
      })

      const CONDITIONAL_PARAMETERS = [
        OKR_ELEMENT_KEYS.DUE_DATE_MANUAL,
        OKR_ELEMENT_KEYS.START_DATE_MANUAL
      ]

      const isAllConditionalParametersDefined = CONDITIONAL_PARAMETERS.every(
        param => !isUndefined(updatedOkrElementInfo[param])
      )

      if (isAllConditionalParametersDefined) {
        CONDITIONAL_PARAMETERS.forEach(param => {
          this.listState.okrElements[elementId][param] = updatedOkrElementInfo[param]
        })
      }

      this.$emit('on-okr-element-updated', updatedOkrElementInfo)
    }
  }
})
</script>

<style lang="scss" scoped></style>
